// src/contexts/EntitlementsContext.jsx
// Bulletproof entitlements for Trial + Promo + Paid access (authoritative: users_public.access_until)
// - Calls public.ensure_trial_started() on login (idempotent)
// - Reads users_public.trial_started_at / trial_ends_at / access_until
// - Caches to localStorage for instant UI on reload
// - LIVE countdown: ticks every 30s so TopBar updates without reload
// - Provides: isLocked, reason, daysRemaining, trialDaysRemaining, accessUntilMs, accessSource
//
// NOTE: requires an RPC named ensure_trial_started() in Supabase.
// It MUST set trial_started_at/trial_ends_at/access_until for first-time users safely.

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { supabase } from "../supabaseClient";
import { useAuth } from "./AuthContext";
import { getUserEntitlements } from "../lib/entitlementsCache";

const EntCtx = createContext(null);

const GLOBAL_LAST_LOADED_USER_KEY = "__mudo_entitlements_last_loaded_user__";
const GLOBAL_LAST_ENSURED_USER_KEY = "__mudo_entitlements_last_ensured_user__";
const isDev = Boolean(import.meta?.env?.DEV);

const DAY_MS = 1000 * 60 * 60 * 24;

function safeJsonParse(raw, fallback = null) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function toMs(v) {
  if (!v) return null;
  const d = new Date(v);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}

function ceilDaysLeft(untilMs, nowMs) {
  if (!untilMs) return null;
  const diff = untilMs - nowMs;
  if (!Number.isFinite(diff)) return null;
  if (diff <= 0) return 0;
  return Math.ceil(diff / DAY_MS);
}

function guessAccessSource({ accessUntilMs, trialEndsAtMs }) {
  if (!accessUntilMs || !trialEndsAtMs) return "unknown";
  // if access_until is basically equal to trial_ends_at, treat as trial
  return Math.abs(accessUntilMs - trialEndsAtMs) < 60 * 1000 ? "trial" : "promo";
}

export function EntitlementsProvider({ children }) {
  const auth = useAuth() || {};
  const userId = auth?.user?.id || null;
  const isAuthReady = Boolean(auth?.isAuthReady);

  const [loading, setLoading] = useState(true);

  // Tick makes countdown live
  const [nowTickMs, setNowTickMs] = useState(() => Date.now());

  // Optional semantic fields (you can evolve later)
  const [status, setStatus] = useState("free"); // free | trialing | active | past_due | unpaid | canceled
  const [plan, setPlan] = useState("free"); // free | monthly | annual | studio | studio_annual etc.

  const [trialStartedAtMs, setTrialStartedAtMs] = useState(null);
  const [trialEndsAtMs, setTrialEndsAtMs] = useState(null);
  const [accessUntilMs, setAccessUntilMs] = useState(null);

  const [reason, setReason] = useState(null);

  const lastLoadedForUserRef = useRef(null);
  const inFlightRef = useRef(false);
  const stateRef = useRef({ status: "free", plan: "free", trialStartedAtMs: null, trialEndsAtMs: null, accessUntilMs: null });
  const lastEnsuredTrialForUserRef = useRef(null);
  const refreshScheduledRef = useRef(false);
  const lastRefreshScheduleAtRef = useRef(0);

  // Sync refs with state
  useEffect(() => {
    stateRef.current = { status, plan, trialStartedAtMs, trialEndsAtMs, accessUntilMs };
  }, [status, plan, trialStartedAtMs, trialEndsAtMs, accessUntilMs]);

  // Live ticker (every 30s)
  useEffect(() => {
    const t = setInterval(() => setNowTickMs(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  // Hydrate cache for instant UI
  useEffect(() => {
    const cached = safeJsonParse(localStorage.getItem("mudo:entitlements") || "null", null);
    if (cached) {
      if (cached.status) setStatus(cached.status);
      if (cached.plan) setPlan(cached.plan);
      if (cached.trialStartedAtMs) setTrialStartedAtMs(cached.trialStartedAtMs);
      if (cached.trialEndsAtMs) setTrialEndsAtMs(cached.trialEndsAtMs);
      if (cached.accessUntilMs) setAccessUntilMs(cached.accessUntilMs);
    }
    setLoading(false);
  }, []);

  const writeCache = useCallback(
    (next = {}) => {
      const merged = {
        ...stateRef.current,
        ...next,
        updatedAtMs: Date.now(),
      };

      try {
        localStorage.setItem("mudo:entitlements", JSON.stringify(merged));
      } catch {}

      const current = stateRef.current;
      if (next.status !== undefined && next.status !== current.status) setStatus(next.status);
      if (next.plan !== undefined && next.plan !== current.plan) setPlan(next.plan);
      if (next.trialStartedAtMs !== undefined && next.trialStartedAtMs !== current.trialStartedAtMs) setTrialStartedAtMs(next.trialStartedAtMs);
      if (next.trialEndsAtMs !== undefined && next.trialEndsAtMs !== current.trialEndsAtMs) setTrialEndsAtMs(next.trialEndsAtMs);
      if (next.accessUntilMs !== undefined && next.accessUntilMs !== current.accessUntilMs) setAccessUntilMs(next.accessUntilMs);
    },
    []
  );

  const loadFromDb = useCallback(async (_opts = {}) => {
    if (!supabase) throw new Error("Supabase not configured");
    if (!userId) return;

    // prevent pile-ups if multiple events fire quickly
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      // 1) Ensure trial started (safe to call repeatedly if SQL is idempotent)
      const alreadyEnsured =
        lastEnsuredTrialForUserRef.current === userId ||
        (typeof globalThis !== "undefined" && globalThis[GLOBAL_LAST_ENSURED_USER_KEY] === userId);
      if (!alreadyEnsured) {
        if (isDev) console.log("[Entitlements] Calling ensure_trial_started RPC...");
        const { error: ensureErr } = await supabase.rpc("ensure_trial_started");
        // Prevent spam: only attempt once per user per session (even on error).
        lastEnsuredTrialForUserRef.current = userId;
        if (typeof globalThis !== "undefined") globalThis[GLOBAL_LAST_ENSURED_USER_KEY] = userId;
        if (ensureErr) {
          console.error("[Entitlements] RPC ensure_trial_started failed:", ensureErr);
          // Continue to read anyway
        } else if (isDev) {
          console.log("[Entitlements] RPC ensure_trial_started succeeded");
        }
      }

      // 2) Read authoritative access fields
      if (isDev) console.log("[Entitlements] Querying users_public for userId:", userId);
      const data = await getUserEntitlements(supabase, userId);
      const userAccess = data?.userPublic || null;
      const subscription = data?.subscription || null;
      const hasSubscription = Boolean(subscription?.stripe_subscription_id);

      if (isDev) console.log("[Entitlements] Query result:", {
        hasUserAccess: !!userAccess,
        hasSubscription,
        trial_started_at: userAccess?.trial_started_at,
        trial_ends_at: userAccess?.trial_ends_at,
        access_until: userAccess?.access_until,
      });

      const ts = toMs(userAccess?.trial_started_at);
      const te = toMs(userAccess?.trial_ends_at);
      const au = toMs(userAccess?.access_until);

      if (isDev) console.log("[Entitlements] Converted to ms:", { ts, te, au });

      // Fallback: if access_until is missing, use trial_ends_at as gate
      const finalAccess = au ?? te ?? null;

      const now = Date.now();
      const locked = finalAccess ? now > finalAccess : true;

      const nextReason = locked ? "ACCESS_EXPIRED" : null;
      setReason((prev) => (prev === nextReason ? prev : nextReason));

      const source = guessAccessSource({ accessUntilMs: finalAccess, trialEndsAtMs: te });
      const nextStatus = locked ? "free" : source === "trial" ? "trialing" : "active";
      const nextPlan = hasSubscription ? "paid" : "free";

      if (isDev) console.log("[Entitlements] Final state:", {
        finalAccess,
        locked,
        source,
        nextStatus,
        daysRemainingComputed: finalAccess ? Math.ceil((finalAccess - now) / DAY_MS) : null,
        nextPlan,
        hasSubscription,
      });

      writeCache({
        trialStartedAtMs: ts,
        trialEndsAtMs: te,
        accessUntilMs: finalAccess,
        status: nextStatus,
        plan: nextPlan,
      });
    } finally {
      inFlightRef.current = false;
    }
  }, [userId]);

  // Load when auth becomes ready and user changes
  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (isDev) console.log("[Entitlements] Load effect running:", { isAuthReady, userId });
      if (!isAuthReady) {
        if (isDev) console.log("[Entitlements] Auth not ready, skipping");
        return;
      }

      if (!userId) {
        if (isDev) console.log("[Entitlements] No userId, clearing state");
        lastLoadedForUserRef.current = null;
        lastEnsuredTrialForUserRef.current = null;
        if (typeof globalThis !== "undefined") {
          globalThis[GLOBAL_LAST_LOADED_USER_KEY] = null;
          globalThis[GLOBAL_LAST_ENSURED_USER_KEY] = null;
        }
        setReason(null);
        writeCache({
          status: "free",
          plan: "free",
          trialStartedAtMs: null,
          trialEndsAtMs: null,
          accessUntilMs: null,
        });
        return;
      }

      // avoid repeated identical loads
      const alreadyLoaded =
        lastLoadedForUserRef.current === userId ||
        (typeof globalThis !== "undefined" && globalThis[GLOBAL_LAST_LOADED_USER_KEY] === userId);
      if (alreadyLoaded) {
        if (isDev) console.log("[Entitlements] Already loaded for this user, skipping");
        return;
      }
      lastLoadedForUserRef.current = userId;
      if (typeof globalThis !== "undefined") globalThis[GLOBAL_LAST_LOADED_USER_KEY] = userId;

      try {
        setLoading(true);
        if (isDev) console.log("[Entitlements] Starting loadFromDb");
        await loadFromDb({ reason: "auth-ready" });
      } catch (e) {
        console.error("[Entitlements] load failed:", e);
        if (!alive) return;
        // Keep cached data if any
        setReason(null);
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();

    return () => {
      alive = false;
    };
  }, [isAuthReady, userId, loadFromDb]);

  // Refresh on focus / visibility / manual event
  useEffect(() => {
    if (!isAuthReady || !userId) return;

    const scheduleRefresh = () => {
      if (inFlightRef.current) return;
      if (refreshScheduledRef.current) return;
      const now = Date.now();
      // Coalesce focus + visibilitychange bursts (and any rapid repeats)
      if (now - lastRefreshScheduleAtRef.current < 1500) return;
      lastRefreshScheduleAtRef.current = now;
      refreshScheduledRef.current = true;
      setTimeout(() => {
        refreshScheduledRef.current = false;
        loadFromDb({ reason: "focus" }).catch(() => {});
      }, 0);
    };

    const onFocus = () => scheduleRefresh();

    const onVis = () => {
      if (document.visibilityState === "visible") scheduleRefresh();
    };

    const onManualRefresh = () => {
      // Manual refresh should not be coalesced/throttled.
      loadFromDb({ reason: "manual" }).catch(() => {});
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("mudo:entitlements-refresh", onManualRefresh);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("mudo:entitlements-refresh", onManualRefresh);
    };
  }, [isAuthReady, userId, loadFromDb]);

  // Derived values (LIVE)
  const daysRemaining = useMemo(
    () => ceilDaysLeft(accessUntilMs, nowTickMs),
    [accessUntilMs, nowTickMs]
  );

  const trialDaysRemaining = useMemo(
    () => ceilDaysLeft(trialEndsAtMs, nowTickMs),
    [trialEndsAtMs, nowTickMs]
  );

  const isLocked = useMemo(() => {
    if (!accessUntilMs) return true;
    return nowTickMs > accessUntilMs;
  }, [accessUntilMs, nowTickMs]);

  // Avoid render-time logging; log only when derived primitives change.
  useEffect(() => {
    if (!isDev) return;
    console.log("[Entitlements] Current state:", {
      accessUntilMs,
      trialEndsAtMs,
      daysRemaining,
      isLocked,
    });
  }, [accessUntilMs, trialEndsAtMs, daysRemaining, isLocked]);

  const accessSource = useMemo(
    () => guessAccessSource({ accessUntilMs, trialEndsAtMs }),
    [accessUntilMs, trialEndsAtMs]
  );

  // Derived accessLabel for UI display
  const accessLabel = useMemo(() => {
    if (isLocked) return "Expired";
    if (accessSource === "trial") return "Trial";
    if (accessSource === "promo") return "Promo";
    return "Active";
  }, [isLocked, accessSource]);

  const features = useMemo(() => {
    const pro = !isLocked;
    return {
      plannerPro: pro,
      driveSync: pro,
      aiAssistant: pro,
      analytics: pro,
    };
  }, [isLocked]);

  const refresh = useCallback(async () => {
    if (!userId) return;
    await loadFromDb();
  }, [userId, loadFromDb]);

  const value = {
    loading,
    status,
    plan,

    trialStartedAtMs,
    trialEndsAtMs,
    accessUntilMs,

    daysRemaining,
    trialDaysRemaining,
    accessSource,
    accessLabel,

    isLocked,
    reason,

    setStatus,
    setPlan,
    writeCache,
    refresh,

    features,
  };

  return <EntCtx.Provider value={value}>{children}</EntCtx.Provider>;
}

export const useEntitlements = () => useContext(EntCtx);
